/**
 * Pure rules of the space context (#152), kept apart from the store so they can be tested
 * without pinia, the router or the API.
 */

export const ALL_SPACES = 'all'
export const RECENT_MAX = 2
export const SPACE_FILTER_PARAM = 'filter[space]'
/** The parameter before the team → space rename (#162): dropped wherever the filter is written. */
export const LEGACY_SPACE_FILTER_PARAM = 'filter[team]'

export interface SpaceEntry {
  id: string
  name: string
  isMember: boolean
  /** Whether non-members may create tasks and protocols in it (default true). */
  allowNonMemberCreate: boolean
  /** Own code for the mark, null for initials from the name. */
  short: string | null
  logoUrl: string | null
  /** `#RRGGBB` of the mark, null on spaces an older backend sends without one. */
  color: string | null
  hidden: boolean
}

export interface PersistedContext {
  currentId: string | null
  recent: string[]
}

interface RawTeam {
  id: string
  name: string
  portal?: boolean
  kind?: string
  archived_at?: number | null
  allow_non_member_create?: boolean
  hidden?: boolean
  short?: string | null
  logo_url?: string | null
  color?: string | null
  members?: { user_id: string }[]
}

/** Teams as the switcher lists them: no portals, no archived spaces, sorted by name. */
export function toSpaces(teams: RawTeam[], userId: string | null): SpaceEntry[] {
  return teams
    .filter((team) => !team.portal && team.kind !== 'portal' && !team.archived_at)
    .map((team) => ({
      id: team.id,
      name: team.name,
      isMember: !!userId && (team.members ?? []).some((member) => member.user_id === userId),
      // A hidden space never lets non-members create, whatever an older backend still says.
      allowNonMemberCreate: team.allow_non_member_create !== false && !team.hidden,
      short: team.short || null,
      logoUrl: team.logo_url || null,
      color: team.color || null,
      hidden: team.hidden === true,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'))
}

export function storageKey(accountId: string, userId: string): string {
  return `space-context.${accountId}.${userId}`
}

export function readPersisted(raw: string | null): PersistedContext {
  try {
    const parsed = raw ? JSON.parse(raw) : null
    return {
      currentId: typeof parsed?.currentId === 'string' ? parsed.currentId : null,
      recent: Array.isArray(parsed?.recent) ? parsed.recent.filter((id: unknown) => typeof id === 'string') : [],
    }
  } catch {
    return { currentId: null, recent: [] }
  }
}

/**
 * The backend's stored context as the store reads it, or null when there is none to go by (the
 * request failed, or the user never chose a space — then the browser's copy, if any, counts).
 */
export function serverContext(stored: { current_id: string | null; recent: string[] } | null): PersistedContext | null {
  if (!stored || (stored.current_id === null && stored.recent.length === 0)) return null
  return { currentId: stored.current_id, recent: stored.recent.filter((id) => typeof id === 'string') }
}

/**
 * The context to start with: the stored one if that space still exists, otherwise the only own
 * space when there is exactly one, otherwise all spaces.
 */
export function initialCurrent(persisted: PersistedContext, spaces: SpaceEntry[]): string {
  if (persisted.currentId === ALL_SPACES) return ALL_SPACES
  if (persisted.currentId && spaces.some((space) => space.id === persisted.currentId)) return persisted.currentId
  const mine = spaces.filter((space) => space.isMember)
  return mine.length === 1 ? mine[0].id : ALL_SPACES
}

/** Most recent first, without the given one twice, at most RECENT_MAX, never "all". */
export function pushRecent(recent: string[], id: string): string[] {
  if (id === ALL_SPACES) return recent
  return [id, ...recent.filter((other) => other !== id)].slice(0, RECENT_MAX)
}

/**
 * Where creating is offered: own spaces, spaces that let non-members create, and for managers
 * every space — the same rule the backend enforces (#141).
 */
export function canCreateIn(space: SpaceEntry, isManager: boolean): boolean {
  return isManager || space.isMember || space.allowNonMemberCreate
}

/**
 * A stored list filter after the context changed: the team filter follows the context, every
 * other filter stays. Returns null when nothing is left to store.
 */
export function withContextSpace(
  stored: Record<string, string> | null,
  currentId: string,
): Record<string, string> | null {
  const next: Record<string, string> = { ...(stored ?? {}) }
  delete next[LEGACY_SPACE_FILTER_PARAM]
  if (currentId === ALL_SPACES) {
    delete next[SPACE_FILTER_PARAM]
  } else {
    next[SPACE_FILTER_PARAM] = currentId
  }
  return Object.keys(next).length ? next : null
}
